import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Pin, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useConciergeStore } from '@/stores/useConciergeStore'

/**
 * Compact dropdown of recent sessions + "New conversation". Spare
 * styling: no rounded corners, hairline separators, dark surface
 * matching the homepage.
 */
export function ConciergeSessionsPicker() {
  const { sessions, sessionId, loadSession, newSession, togglePin } = useConciergeStore()
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const active = sessions.find((s) => s.session_id === sessionId)
  const label = active?.name?.trim() || 'Concierge'

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 text-[14px] text-foreground transition-opacity',
          open ? 'opacity-100' : 'opacity-80 hover:opacity-100'
        )}
      >
        <span className="truncate max-w-[200px]">{label}</span>
        <ChevronDown
          size={12}
          className={cn(
            'text-muted-foreground transition-transform duration-150',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 max-h-[400px] overflow-y-auto bg-[rgb(28,28,28)] border border-border z-20">
          <button
            type="button"
            onClick={() => {
              newSession()
              setOpen(false)
            }}
            className="w-full px-3 py-2.5 flex items-center gap-2 text-[14px] text-foreground hover:bg-[color-mix(in_srgb,white_4%,transparent)] text-left border-b border-border"
          >
            <Plus size={14} strokeWidth={1.5} />
            <span>New conversation</span>
          </button>
          {sessions.map((s) => (
            <div
              key={s.session_id}
              className={cn(
                'group w-full flex items-stretch text-[14px] text-foreground border-b border-border last:border-b-0 hover:bg-[color-mix(in_srgb,white_4%,transparent)]',
                s.session_id === sessionId && 'bg-[color-mix(in_srgb,white_4%,transparent)]'
              )}
            >
              <button
                type="button"
                onClick={() => {
                  loadSession(s.session_id)
                  setOpen(false)
                }}
                className="min-w-0 flex-1 px-3 py-2.5 flex items-start gap-2 text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    {s.name?.trim() || 'Untitled'}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-light mt-0.5">
                    {s.total_turns} turn{s.total_turns === 1 ? '' : 's'}
                    {s.last_message_at && (
                      <>
                        {' · '}
                        {new Date(s.last_message_at).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </>
                    )}
                  </div>
                </div>
              </button>
              <button
                type="button"
                title={s.pinned ? 'Unpin' : 'Pin to top'}
                aria-label={s.pinned ? 'Unpin session' : 'Pin session to top'}
                onClick={(e) => {
                  e.stopPropagation()
                  togglePin(s.session_id)
                }}
                className={cn(
                  'shrink-0 px-2.5 flex items-center transition-opacity',
                  s.pinned
                    ? 'opacity-100 text-foreground'
                    : 'opacity-0 group-hover:opacity-50 hover:!opacity-100 text-muted-foreground'
                )}
              >
                <Pin size={13} className={s.pinned ? 'fill-current' : ''} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
